import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { RealtimeChannel } from '@supabase/supabase-js';

export const getConversationId = (type: 'channel' | 'dm', id: string): string => `${type}:${id}`;

export const useUnreadIndicator = (activeConversationId: string | null) => {
  const { user } = useAuth();
  const [unreadConversations, setUnreadConversations] = useState<Set<string>>(new Set());
  const subscriptionRef = useRef<RealtimeChannel | null>(null);
  const activeConversationIdRef = useRef(activeConversationId);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    if (!user?.id) {
      setUnreadConversations(new Set());
      return;
    }

    const handleNewMessage = (payload: any) => {
      const newMessage = payload.new;
      if (!user?.id) return;

      let messageConversationId: string | null = null;
      if (newMessage.channel_id) {
        messageConversationId = getConversationId('channel', newMessage.channel_id);
      } else if (newMessage.recipient_id === user.id && newMessage.sender_id) {
        messageConversationId = getConversationId('dm', newMessage.sender_id);
      } else {
        return;
      }

      if (messageConversationId && messageConversationId !== activeConversationIdRef.current) {
        setUnreadConversations(prev => new Set(prev).add(messageConversationId!));
      }
    };

    const subscription = supabase
      .channel('public:messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, handleNewMessage)
      .subscribe();

    subscriptionRef.current = subscription;

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [user?.id]);

  const clearUnread = (conversationId: string | null) => {
    if (!conversationId) return;
    setUnreadConversations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conversationId)) {
        newSet.delete(conversationId);
        return newSet;
      }
      return prev;
    });
  };

  return { unreadConversations, clearUnread }; 
};
